import type { QuoteModeValue } from './schemas/quote';

export interface ComputableQuoteLine {
  mode: QuoteModeValue;
  quantity: number | null;
  unitPrice: number | null;
  adjustment: number | null;
  isProposedForDeletion: boolean;
}

export interface ComputedLine {
  subtotal: number;
  isPricingComplete: boolean;
}

export interface QuoteTotalsResult {
  subtotalHt: number;
  globalAdjustment: number;
  totalHt: number;
  lineSubtotals: number[];
  allLinesPriced: boolean;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function quoteModeAllowsAdjustment(mode: QuoteModeValue): boolean {
  return mode === 'TIME_BASED_DAY' || mode === 'TIME_BASED_HOUR';
}

export function effectiveGlobalAdjustment(
  mode: QuoteModeValue,
  globalAdjustment: number | null,
): number {
  if (!quoteModeAllowsAdjustment(mode)) return 0;
  return isFiniteNumber(globalAdjustment) ? globalAdjustment : 0;
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

export function isQuoteLinePriced(line: {
  mode: QuoteModeValue;
  quantity: unknown;
  unitPrice: unknown;
}): boolean {
  const hasUnitPrice = line.unitPrice !== null && line.unitPrice !== undefined;
  if (line.mode === 'TIME_BASED_DAY' || line.mode === 'TIME_BASED_HOUR') {
    return hasUnitPrice && line.quantity !== null && line.quantity !== undefined;
  }
  return hasUnitPrice;
}

export function computeLineSubtotal(line: ComputableQuoteLine): ComputedLine {
  if (line.isProposedForDeletion) {
    return { subtotal: 0, isPricingComplete: true };
  }
  if (line.mode === 'TIME_BASED_DAY' || line.mode === 'TIME_BASED_HOUR') {
    const qty = line.quantity;
    const unit = line.unitPrice;
    if (!isFiniteNumber(qty) || !isFiniteNumber(unit)) {
      return { subtotal: 0, isPricingComplete: false };
    }
    const adjustment = isFiniteNumber(line.adjustment) ? line.adjustment : 0;
    const subtotal = roundCents(qty * unit + adjustment);
    return { subtotal, isPricingComplete: true };
  }
  if (line.mode === 'PER_LINE') {
    const unit = line.unitPrice;
    if (!isFiniteNumber(unit)) {
      return { subtotal: 0, isPricingComplete: false };
    }
    return { subtotal: roundCents(unit), isPricingComplete: true };
  }
  const unit = line.unitPrice;
  if (!isFiniteNumber(unit)) {
    return { subtotal: 0, isPricingComplete: false };
  }
  return { subtotal: roundCents(unit), isPricingComplete: true };
}

export function computeQuoteTotals(
  lines: ComputableQuoteLine[],
  globalAdjustment: number | null = 0,
): QuoteTotalsResult {
  const lineSubtotals: number[] = [];
  let subtotalHt = 0;
  let allLinesPriced = true;
  for (const line of lines) {
    const result = computeLineSubtotal(line);
    lineSubtotals.push(result.subtotal);
    subtotalHt += result.subtotal;
    if (!result.isPricingComplete) allLinesPriced = false;
  }
  const adjustment = isFiniteNumber(globalAdjustment) ? roundCents(globalAdjustment) : 0;
  return {
    subtotalHt: roundCents(subtotalHt),
    globalAdjustment: adjustment,
    totalHt: roundCents(subtotalHt + adjustment),
    lineSubtotals,
    allLinesPriced,
  };
}
